import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { FooterLogo } from "./BrandLogo";
import { Download, Share, Plus, X } from "lucide-react";
import {
  installMode, isStandalone, triggerInstall, dismissedRecently, setDismissedAt,
  currentSuppress, trackInstall,
} from "../lib/pwaInstall";

// Only the customer-facing app may show the install invitation.
function isCustomerRoute(pathname) {
  let base = pathname;
  if (base === "/en" || base.startsWith("/en/")) base = base.slice(3) || "/";
  const seg = base.split("/").filter(Boolean);
  if (seg.length === 0) return true;                       // "/"
  if (seg[0] === "restaurants" && seg.length === 1) return true;
  if (seg[0] === "order" && seg.length === 2) return true; // /order/:id
  // Customer restaurant page is /restaurant/:slug (2 segments). Staff panels are /restaurant/:rid/section.
  if (seg[0] === "restaurant" && seg.length === 2 && seg[1] !== "login") return true;
  return false;
}

const ENGAGE_KEY = "ponesi_pwa_engaged";
const DWELL_MS = 25000;

export default function PwaInstallPrompt() {
  const { t } = useI18n();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [forced, setForced] = useState(false);
  const [suppressed, setSuppressed] = useState(currentSuppress() > 0);
  const [engaged, setEngaged] = useState(() => {
    try { return sessionStorage.getItem(ENGAGE_KEY) === "1"; } catch { return false; }
  });
  const shownRef = useRef(false);

  const customer = isCustomerRoute(location.pathname);

  // Track suppression from order-critical modals.
  useEffect(() => {
    const onSup = (e) => setSuppressed((e.detail ?? currentSuppress()) > 0);
    window.addEventListener("ponesi-install-suppress", onSup);
    return () => window.removeEventListener("ponesi-install-suppress", onSup);
  }, []);

  // Hide immediately once installed.
  useEffect(() => {
    const onInstalled = () => { setVisible(false); };
    window.addEventListener("ponesi-installed", onInstalled);
    return () => window.removeEventListener("ponesi-installed", onInstalled);
  }, []);

  // Manual open from Settings — bypasses engagement + cooldown (still respects installed/suppress).
  useEffect(() => {
    const onOpen = () => {
      if (isStandalone() || installMode() === "none") return;
      setForced(true);
      setVisible(true);
      trackInstall("PWA_INSTALL_PROMPT_SHOWN", { source: "settings", mode: installMode() });
    };
    window.addEventListener("ponesi-open-install", onOpen);
    return () => window.removeEventListener("ponesi-open-install", onOpen);
  }, []);

  // Engagement: visiting a restaurant/menu page, or dwelling ~25s.
  useEffect(() => {
    const seg = location.pathname.replace(/^\/en/, "").split("/").filter(Boolean);
    if (seg[0] === "restaurant" && seg.length === 2 && seg[1] !== "login") {
      setEngaged(true);
      try { sessionStorage.setItem(ENGAGE_KEY, "1"); } catch {}
    }
  }, [location.pathname]);

  useEffect(() => {
    if (engaged) return;
    const id = setTimeout(() => {
      setEngaged(true);
      try { sessionStorage.setItem(ENGAGE_KEY, "1"); } catch {}
    }, DWELL_MS);
    return () => clearTimeout(id);
  }, [engaged]);

  // Auto-show decision.
  useEffect(() => {
    if (visible || forced) return;
    if (!customer || suppressed || !engaged) return;
    if (isStandalone()) return;
    if (dismissedRecently()) return;
    const mode = installMode();
    if (mode === "installed" || mode === "none") return;
    if (shownRef.current) return;
    shownRef.current = true;
    setVisible(true);
    trackInstall("PWA_INSTALL_PROMPT_SHOWN", { source: "auto", mode });
    if (mode === "ios") trackInstall("PWA_IOS_INSTRUCTIONS_SHOWN");
  }, [visible, forced, customer, suppressed, engaged, location.pathname]);

  const close = () => {
    setVisible(false);
    setForced(false);
    setDismissedAt();
    trackInstall("PWA_INSTALL_DISMISSED");
  };

  const doInstall = async () => {
    trackInstall("PWA_INSTALL_CLICKED");
    const choice = await triggerInstall();
    if (choice.outcome === "accepted") {
      trackInstall("PWA_INSTALLED");
      setVisible(false);
      setForced(false);
    }
  };

  if (!visible || !customer || suppressed || isStandalone()) return null;

  const mode = installMode();
  if (mode === "installed" || mode === "none") return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 max-w-md mx-auto px-3 pb-[calc(env(safe-area-inset-bottom)+12px)]"
      data-testid="pwa-install-prompt" role="dialog" aria-label={t("pwa.title", "Ponesi ti je na dohvat ruke")}>
      <div className="bg-white rounded-3xl shadow-[0_-8px_40px_rgb(0,0,0,0.18)] border border-slate-100 p-5 animate-slide-up relative">
        <button data-testid="pwa-install-close" onClick={close} aria-label={t("pwa.dismiss", "Ne sada")}
          className="absolute top-3 right-3 bg-slate-100 rounded-full p-1.5 active:scale-95 transition-transform">
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <FooterLogo height={52} className="shrink-0" />
          <div className="min-w-0">
            <h3 className="font-display font-black text-lg leading-tight">
              {mode === "ios" ? t("pwa.ios_title", "Dodaj Ponesi na početni ekran")
                : mode === "ios-inapp" ? t("pwa.ios_title", "Dodaj Ponesi na početni ekran")
                : t("pwa.title", "Ponesi ti je na dohvat ruke")}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {mode === "ios-inapp"
                ? t("pwa.inapp_desc", "Otvorite Ponesi u Safariju da biste ga dodali na početni ekran.")
                : t("pwa.desc", "Dodaj Ponesi na početni ekran za brže poručivanje.")}
            </p>
          </div>
        </div>

        {mode === "ios" && (
          <ol className="mt-4 space-y-2.5 text-sm" data-testid="pwa-ios-instructions">
            <li className="flex items-center gap-2.5">
              <span className="w-6 h-6 shrink-0 rounded-full bg-brand-red/10 text-brand-red font-bold flex items-center justify-center text-xs">1</span>
              <span className="flex items-center gap-1.5">{t("pwa.ios_1a", "Dodirni")} <Share size={16} className="text-brand-red" /> {t("pwa.ios_1b", "u Safariju")}</span>
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-6 h-6 shrink-0 rounded-full bg-brand-red/10 text-brand-red font-bold flex items-center justify-center text-xs">2</span>
              <span className="flex items-center gap-1.5">{t("pwa.ios_2a", "Izaberi")} <Plus size={15} className="text-brand-red" /> <b>{t("pwa.ios_2b", "Add to Home Screen")}</b></span>
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-6 h-6 shrink-0 rounded-full bg-brand-red/10 text-brand-red font-bold flex items-center justify-center text-xs">3</span>
              <span>{t("pwa.ios_3", "Potvrdi „Add“ / „Dodaj“")}</span>
            </li>
          </ol>
        )}

        <div className="mt-4 flex items-center gap-2">
          {mode === "android" && (
            <button data-testid="pwa-install-btn" onClick={doInstall}
              className="flex-1 bg-brand-red text-white rounded-full py-3 font-display font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              <Download size={18} /> {t("pwa.install", "Instaliraj Ponesi")}
            </button>
          )}
          <button data-testid="pwa-install-dismiss" onClick={close}
            className={`${mode === "android" ? "px-5" : "flex-1"} rounded-full py-3 font-semibold text-slate-500 bg-slate-100 active:scale-[0.98] transition-transform`}>
            {mode === "android" ? t("pwa.dismiss", "Ne sada") : t("pwa.got_it", "U redu")}
          </button>
        </div>
      </div>
    </div>
  );
}
